'use client';

import { type FC, useMemo, useState } from 'react';
import type { FilesProps } from './Files.types';
import { Button, ButtonGroup, Input, Flex, Text, File } from '@/_components';
import styles from './Files.module.css';
import { sanitizeName, validateName } from '@/_utils/sanitize';
import { getExtensionOfFilename } from '@/_utils/files';

export const Files: FC<FilesProps> = ({
  fileWrappers,
  title,
  isLoading,
  onEditFile,
  onRemoveFile,
}) => {
  const [editingKey, setEditingKey] = useState<number>(-1);
  const [newFileName, setNewFileName] = useState<string>('');

  const validation = useMemo(() => validateName(newFileName), [newFileName]);

  const onCloseNameUpdate = () => {
    setNewFileName('');
    setEditingKey(-1);
  };
  const onFileNameChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    setNewFileName(event.target.value);

  if (!fileWrappers?.length) return null;

  return (
    <Flex isColumn={true} gap={2}>
      {title && (
        <Text type="small" color="secondary">
          {title}
        </Text>
      )}
      <Flex isColumn={true} gap={2} className={styles.files}>
        {fileWrappers?.map((wrapper, key) => {
          const hasNewName = !!wrapper.name;
          const removeFile = () => onRemoveFile(key);
          const onEditFileName = () => {
            const fileName: string =
              (hasNewName ? wrapper.name : wrapper.file.name).replace(
                /\.[^/.]+$/,
                '',
              ) || '';

            setNewFileName(fileName);
            setEditingKey(key);
          };

          const originalExtension: string = getExtensionOfFilename(
            hasNewName ? wrapper.name : wrapper.file?.name,
          );

          const onUpdateName = () => {
            const sanitizedName: string = sanitizeName(
              newFileName,
              hasNewName ? wrapper.name : wrapper.file?.name,
            );

            if (!!sanitizedName) {
              onEditFile(key, { file: wrapper.file, name: sanitizedName });
            }

            onCloseNameUpdate();
          };

          return editingKey === key ? (
            <Flex key={key} gap={6} isYCentered={true} isBetween={true}>
              <Flex gap={4} yAlign="baseline" isFullWidth={true}>
                <Input
                  onChange={onFileNameChange}
                  placeholder="File name"
                  value={newFileName}
                  isLabelHidden={true}
                  label="New File Name"
                  helperText={
                    !validation?.isValid ? validation?.error : undefined
                  }
                  helperTextColor={
                    !validation?.isValid ? 'critical' : undefined
                  }
                />
                {originalExtension && (
                  <Flex className={styles.extensionText} isCentered={true}>
                    <Text type="small">.{originalExtension}</Text>
                  </Flex>
                )}
              </Flex>
              <ButtonGroup isSameLine={true} isSmallGroup={true}>
                <Button
                  intent="ghost"
                  size="small"
                  value="Cancel"
                  onClick={onCloseNameUpdate}
                />
                <Button
                  intent="primary"
                  isDisabled={!newFileName}
                  size="small"
                  value="Save"
                  onClick={onUpdateName}
                />
              </ButtonGroup>
            </Flex>
          ) : (
            <File
              key={key}
              title={hasNewName ? wrapper.name : wrapper.file.name}
              isEditing={editingKey === key}
            >
              <Button
                iconLeft="pencil"
                intent="ghost"
                size="tiny"
                onClick={onEditFileName}
                value="Edit"
                isIconOnly={true}
              />
              <Button
                value="Remove"
                intent="danger-secondary"
                isDisabled={isLoading}
                size="tiny"
                onClick={removeFile}
                isIconOnly={true}
                iconLeft="trash"
              />
            </File>
          );
        })}
      </Flex>
    </Flex>
  );
};
